import {
  CashApp,
  Email,
  LandingPageOptions,
  LandingPageValidator,
  Optional,
  Phone,
  SMS,
  Venmo,
  WiFi
} from '../types'
import {
  cashAppSchema,
  emailSchema,
  phoneSchema,
  smsSchema,
  venmoSchema,
  wifiSchema
} from '../schemas'
import {
  getCashAppParams,
  getEmailParams,
  getPhoneParams,
  getSMSParams,
  getVenmoParams,
  getWiFiParams
} from '../utils'
import validateIsMobilePhone from 'validator/lib/isMobilePhone'
import validateIsUrl from 'validator/lib/isURL'

export const isSMSShortCode = (shortCode: string): boolean => /^\d{5,6}$/.test(shortCode)

export const isURL = (text: string, requireProtocol?: boolean): boolean =>
  validateIsUrl(text, {
    require_protocol: requireProtocol,
    require_valid_protocol: requireProtocol,
    allow_underscores: true
  })

export const isEmail = (data: Optional<Email>): boolean => emailSchema.isValidSync(data)

export const isSMS = (data: Optional<SMS>): boolean => {
  const phoneNumber = data?.phoneNumber || ''
  const isPhoneNumberValid = validateIsMobilePhone(phoneNumber) || isSMSShortCode(phoneNumber)
  return isPhoneNumberValid && smsSchema.isValidSync(data)
}

/**
 * Checks if string starts with numbers and ends with an optional # symbol
 * @param extension
 * @returns
 */
const getIsExtensionValid = (extension: string): boolean => {
  return /^,*\d+#{0,1}$/.test(extension)
}

// isMobilePhone is deprecated, used isSMS
export const isMobilePhone = (data: Optional<SMS>): boolean => isSMS(data)

export const isPhone = (data: Optional<Phone>): boolean => {
  const [phoneNumber, extension] = (data?.phoneNumber || '').split(/,(.+)/)
  const isValidNumber = validateIsMobilePhone(phoneNumber)
  const isValidExtension = !extension || getIsExtensionValid(extension)
  return isValidNumber && isValidExtension && phoneSchema.isValidSync(data)
}

// TODO: deconstruct and validate venmo url
export const isVenmo = (data: Optional<Venmo>): boolean => {
  return venmoSchema.isValidSync(data)
}

export const isWiFi = (data: Optional<WiFi>): boolean => {
  return wifiSchema.isValidSync(data)
}
export const isCashApp = (data: Optional<CashApp>): boolean => {
  return cashAppSchema.isValidSync(data)
}

export const landingPageValidator = ({
  id,
  url = '',
  data = '',
  options
}: LandingPageValidator): boolean => {
  switch (id) {
    case LandingPageOptions.EMAIL:
      if (typeof data === 'string') data = getEmailParams(data)
      return isEmail(data as Email)
    case LandingPageOptions.SMS:
      if (typeof data === 'string') data = getSMSParams(data)
      return isSMS(data as SMS)
    case LandingPageOptions.PHONE:
      if (typeof data === 'string') data = getPhoneParams(data)
      return isPhone(data as Phone)
    case LandingPageOptions.VENMO:
      if (typeof data === 'string') data = getVenmoParams(data)
      return isVenmo(data as Venmo)
    case LandingPageOptions.WIFI:
      if (typeof data === 'string') {
        const wifiData = getWiFiParams(data)
        return isWiFi(wifiData as WiFi)
      }
      return isWiFi(data as WiFi)
    case LandingPageOptions.CASH_APP:
      if (typeof data === 'string') data = getCashAppParams(data)
      return isCashApp(data as CashApp)
    case LandingPageOptions.URL:
      return isURL(url, !!options?.requireProtocol)
    default:
      return false
  }
}
